import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";

import { RouterProvider } from "react-router";

import type { DevelopmentIdentity } from "@/auth/development-auth";
import { type EntraAppClient, EntraAuthProvider, useEntraAuth } from "@/auth/entra-auth-context";
import { type EntraBrowserConfig, resolveEntraBrowserConfig } from "@/auth/entra-browser";
import { Alert, Button, FormField, SelectField } from "@/components/ui";
import {
  PlatformApiError,
  PlatformSessionProvider,
  selectPlatformRuntime,
  usePlatformSession,
} from "@/platform";
import type { createAppRouter } from "@/router/routes";
import { tenantSlugFromPath } from "@/server/tenant-addressing";
import { setTelemetryUserId } from "@/telemetry/user-context-span-processor";

import { AuthShellProvider } from "./auth-shell-context";
import { AuthScreen } from "./AuthScreen";
import { TelemetryBoundary } from "./TelemetryBoundary";

export interface DevelopmentAuthUi {
  identities: readonly DevelopmentIdentity[];
  selectedIdentityId: string;
  signIn: (identityId: string) => void;
}

type AppRouter = ReturnType<typeof createAppRouter>;

/**
 * Reports a session refused for the tenant the path names. Laid out before
 * paint, so the refusal is never drawn before the gate replaces it.
 */
function AddressedTenantRefusal({ onRefused }: { onRefused: () => void }): null {
  const { status, error } = usePlatformSession();
  const refused = status === "error" && error instanceof PlatformApiError && error.status === 403;
  useLayoutEffect(() => {
    if (refused) {
      onRefused();
    }
  }, [refused, onRefused]);
  return null;
}

export function resolveConfiguredEntra(): EntraBrowserConfig | undefined {
  return resolveEntraBrowserConfig(
    import.meta.env as unknown as Record<string, string | undefined>,
    window.location.origin,
  );
}

/**
 * Gate the authenticated workspace on the Entra sign-in lifecycle.
 *
 * Each {@link EntraAuthStatus} renders a distinct screen: a holding state while
 * MSAL initializes, an interactive sign-in prompt when no account is present, a
 * recoverable error state, and — once authenticated — the workspace driven by an
 * HTTP platform client that presents the account's access token per request.
 */
function EntraSessionGate({
  config,
  selectRuntime,
  developmentAuth = undefined,
  router,
  createRouter = undefined,
}: {
  config: EntraBrowserConfig;
  selectRuntime: typeof selectPlatformRuntime;
  developmentAuth?: DevelopmentAuthUi;
  router: AppRouter;
  createRouter?: (basename: string) => AppRouter;
}) {
  const auth = useEntraAuth();
  const [BannerComponent, setBannerComponent] = useState<React.ComponentType | null>(null);

  React.useEffect(() => {
    let active = true;
    if (typeof __IMPERSONATION__ !== "undefined" && __IMPERSONATION__) {
      import("@/components/dev/ImpersonationBanner")
        .then((mod) => {
          if (active) {
            setBannerComponent(() => mod.ImpersonationBanner);
          }
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, []);
  const initialDevelopmentIdentity =
    developmentAuth?.identities.find(
      (identity) => identity.id === developmentAuth.selectedIdentityId,
    ) ?? developmentAuth?.identities[0];
  const [developmentTenantId, setDevelopmentTenantId] = useState(
    initialDevelopmentIdentity?.tenantId ?? "",
  );
  const [pickedDevelopmentIdentityId, setDevelopmentIdentityId] = useState(
    initialDevelopmentIdentity?.id ?? "",
  );
  const developmentTenants = useMemo(
    () =>
      Array.from(
        new Map(
          (developmentAuth?.identities ?? []).map((identity) => [
            identity.tenantId,
            { id: identity.tenantId, name: identity.tenantName },
          ]),
        ).values(),
      ),
    [developmentAuth],
  );
  const developmentUsers = useMemo(
    () =>
      (developmentAuth?.identities ?? []).filter(
        (identity) => identity.tenantId === developmentTenantId,
      ),
    [developmentAuth, developmentTenantId],
  );

  // The identity in force, derived rather than written back. Switching tenant
  // leaves the previous pick pointing at a user this tenant does not have, and
  // the first of the new tenant's users stands in for it until the next pick.
  const developmentIdentityId = developmentUsers.some(
    (identity) => identity.id === pickedDevelopmentIdentityId,
  )
    ? pickedDevelopmentIdentityId
    : (developmentUsers[0]?.id ?? "");

  const selection = useMemo(
    () => selectRuntime({ config, getAccessToken: auth.getAccessToken }),
    [config, auth.getAccessToken, selectRuntime],
  );
  // Where the SPA is served under a tenant path (`/chevron`), that prefix is
  // how the caller named an organisation, and the API learns it from
  // `x-platform-tenant`. A deployment that addresses tenants by host has no
  // such prefix and resolves the tenant server-side from the hostname instead,
  // so this is empty there and the two never both apply.
  const [addressedTenant, setAddressedTenant] = useState(() =>
    typeof window === "undefined" ? undefined : tenantSlugFromPath(window.location.pathname),
  );
  const [rootRouter, setRootRouter] = useState<AppRouter | null>(null);
  // Any unreserved first segment reads as a tenant, and the API refuses an
  // unknown one as it refuses one the caller is not in, so a mistyped page
  // address was refused too. Dropping the segment remounts the router at the
  // root, where `*` answers a page not found.
  const dropAddressedTenant = useCallback(() => {
    if (createRouter) {
      setRootRouter(createRouter("/"));
      setAddressedTenant(undefined);
    }
  }, [createRouter]);
  const client = useMemo(
    () => selection.runtime.createClient({ activeTenantId: addressedTenant }),
    [selection, addressedTenant],
  );

  // Opaque id only, never auth.account?.username — in this app's Entra
  // External ID (CIAM) setup that field is the user's email. auth.userId is
  // already the same opaque id (MSAL's localAccountId/homeAccountId, or the
  // API's own session user id on a cookie-backed host) used as the platform
  // session key below.
  React.useEffect(() => {
    setTelemetryUserId(auth.status === "authenticated" ? (auth.userId ?? undefined) : undefined);
  }, [auth.status, auth.userId]);

  if (auth.status === "initializing") {
    return <AuthScreen title="Signing in" message="Completing sign-in with Microsoft Entra…" />;
  }

  if (auth.status === "error") {
    return (
      <AuthScreen title="Sign-in failed">
        <div className="mt-4">
          <Alert tone="warning">
            {auth.error?.message ?? "Microsoft Entra sign-in could not be completed."}
          </Alert>
        </div>
        <Button
          variant="primary"
          icon="sign-in"
          className="mt-4 w-full"
          onClick={() => auth.login()}
        >
          Try again
        </Button>
      </AuthScreen>
    );
  }

  if (auth.status === "unauthenticated") {
    if (developmentAuth) {
      return (
        <AuthScreen
          title="Choose a local test account"
          message="Select a tenant and user for this local development session."
        >
          <div className="mt-5 space-y-4">
            <FormField label="Tenant" htmlFor="development-tenant">
              <SelectField
                id="development-tenant"
                className="w-full"
                value={developmentTenantId}
                onChange={setDevelopmentTenantId}
                options={developmentTenants.map((tenant) => ({
                  value: tenant.id,
                  label: tenant.name,
                }))}
                data-testid="development-tenant"
              />
            </FormField>
            <FormField label="User" htmlFor="development-user">
              <SelectField
                id="development-user"
                className="w-full"
                value={developmentIdentityId}
                onChange={setDevelopmentIdentityId}
                options={developmentUsers.map((identity) => ({
                  value: identity.id,
                  label: identity.name,
                }))}
                data-testid="development-user"
              />
            </FormField>
            <Button
              variant="primary"
              icon="sign-in"
              className="w-full"
              disabled={!developmentIdentityId}
              onClick={() => developmentAuth.signIn(developmentIdentityId)}
              data-testid="development-sign-in"
            >
              Continue
            </Button>
          </div>
        </AuthScreen>
      );
    }
    return (
      <AuthScreen
        title="Sign in to your workspace"
        message="Use your organisation account to continue."
      >
        <Button
          variant="primary"
          icon="sign-in"
          className="mt-5 w-full"
          onClick={() => auth.login()}
          data-testid="entra-sign-in"
        >
          Sign in with Microsoft
        </Button>
      </AuthScreen>
    );
  }

  return (
    <TelemetryBoundary>
      <PlatformSessionProvider
        client={client}
        // Keyed by tenant too, so dropping it reads as loading, not as the refusal.
        sessionKey={`${
          auth.account?.homeAccountId ?? auth.account?.localAccountId ?? auth.userId ?? "entra"
        }${addressedTenant ? `@${addressedTenant}` : ""}`}
      >
        {addressedTenant && createRouter ? (
          <AddressedTenantRefusal onRefused={dropAddressedTenant} />
        ) : null}
        {BannerComponent ? <BannerComponent /> : null}
        {/* Signed in with an error means a sign out failed and left them here. */}
        {auth.error ? (
          <Alert tone="error">Sign out did not complete: {auth.error.message}</Alert>
        ) : null}
        <AuthShellProvider
          value={{
            profileId: auth.account?.localAccountId ?? auth.userId ?? "",
            onLogout: () => auth.logout(),
            onSwitchUser: () => auth.switchUser(),
          }}
        >
          <RouterProvider router={rootRouter ?? router} />
        </AuthShellProvider>
      </PlatformSessionProvider>
    </TelemetryBoundary>
  );
}

/** Mount the MSAL provider around the {@link EntraSessionGate}. */
export function EntraModeShell({
  config,
  createApp,
  selectRuntime,
  developmentAuth = undefined,
  router,
  createRouter = undefined,
}: {
  config: EntraBrowserConfig;
  createApp: (config: EntraBrowserConfig) => EntraAppClient;
  selectRuntime: typeof selectPlatformRuntime;
  developmentAuth?: DevelopmentAuthUi;
  router: AppRouter;
  createRouter?: (basename: string) => AppRouter;
}) {
  const app = useMemo(() => createApp(config), [createApp, config]);
  return (
    <EntraAuthProvider config={config} app={app}>
      <EntraSessionGate
        config={config}
        selectRuntime={selectRuntime}
        developmentAuth={developmentAuth}
        router={router}
        createRouter={createRouter}
      />
    </EntraAuthProvider>
  );
}

/**
 * Full-screen notice shown when the app has no Entra configuration to sign
 * users in with. Entra is the sole identity provider, so there is no local
 * fallback: the deployment must be configured before anyone can sign in.
 */
export function ConfigurationRequiredScreen() {
  return (
    <AuthScreen
      title="Sign-in is not configured"
      message="This deployment has no Microsoft Entra configuration, so it cannot sign anyone in. Set the Entra environment variables and reload."
    />
  );
}
